/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/AdminDashboard.tsx.
 */

import React, { useState } from "react";
import DashboardHeader from "../../../components/Layout/DashboardHeader";
import SidebarAdmin from "../../../components/Layout/SidebarAdmin";
import DashboardFooter from "../../../components/Layout/DashboardFooter";

import "./Admin.css";

// Existing pages
import AdminProfile from "./Profile/AdminProfile";
import AdminFormManager from "./Form/AdminFormManager";
import AdminDashboardHome from "./Home/AdminDashboardHome";
import AdminUserTable from "./Users/AdminUserTable";
import AdminDropdownManager from "./Dropdowns/AdminDropdownManager";
import AdminManageExperts from "./Experts/AdminManageExperts";

// Material workflow pages
import AdminMaterialOverview from "./Materials/MaterialOverview/AdminMaterialOverview";
import AdminSubmissionDetail from "./Materials/SubmissionDetail/AdminSubmissionDetail";
import AdminSubmissionList from "./Submission/AdminSubmissionList";
import AdminReliabilityStandards from "./ReliabilityStandards/AdminReliabilityStandards";
import AdminPublicPagesManager from "./PublicPages/AdminPublicPagesManager";
import AdminToolCitation from "./ToolCitation/AdminToolCitation";
import AdminEventsManager from "./Events/AdminEventsManager";
import AdminCharacterizationFactors from "./CharacterizationFactors/AdminCharacterizationFactors";
import AdminCalorificValues from "./CalorificValues/AdminCalorificValues";
import AdminFuelDensities from "./FuelDensities/AdminFuelDensities";
import AdminManufacturingProcesses from "./ManufacturingProcesses/AdminManufacturingProcesses";

const AdminDashboard: React.FC = () => {
  const [activeSection, setActiveSection] = useState("Home");

  // Stores selected material ID for overview
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | null>(null);

  // Stores selected submission ID for detailed review
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);

  // Mobile sidebar open/close
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const renderContent = () => {
    // ----------------------------------------------------------------
    // 1️⃣ MATERIAL OVERVIEW — triggered from sidebar "material_#"
    // ----------------------------------------------------------------
    if (activeSection.startsWith("material_")) {
      const materialId = Number(activeSection.split("_")[1]);

      if (selectedMaterialId !== materialId) {
        setSelectedMaterialId(materialId);
        setSelectedSubmissionId(null); // reset detail view when switching materials
      }

      return (
        <>
          {/* Keep overview mounted so its state doesn't reset on "Back" */}
          <div style={{ display: selectedSubmissionId ? "none" : "block" }}>
            <AdminMaterialOverview
              materialId={materialId}
              onOpenSubmission={(id: string) => setSelectedSubmissionId(id)}
            />
          </div>

          {selectedSubmissionId && (
            <AdminSubmissionDetail
              submissionId={selectedSubmissionId}
              onBack={() => setSelectedSubmissionId(null)}
            />
          )}
        </>
      );
    }

    // ----------------------------------------------------------------
    // 2️⃣ NORMAL ADMIN SECTIONS (Home, Profile, Form, etc.)
    // ----------------------------------------------------------------
    switch (activeSection) {
      case "Home":
        return (
          <AdminDashboardHome
            onExploreMaterial={(materialId) => {
              setSelectedSubmissionId(null);
              setIsSidebarOpen(false);
              setActiveSection(`material_${materialId}`);
            }}
          />
        );

      case "Profile":
        return <AdminProfile />;

      case "Manage Users":
        return <AdminUserTable />;

      case "Manage Experts":
        return <AdminManageExperts />;

      case "Form":
        return <AdminFormManager />;

      case "Manage Dropdowns":
        return <AdminDropdownManager />;

      case "Reliability Standards":
        return <AdminReliabilityStandards />;

      case "Characterization factors":
        return <AdminCharacterizationFactors />;

      case "Calorific values":
        return <AdminCalorificValues />;

      case "Fuel densities":
        return <AdminFuelDensities />;

      case "Manufacturing processes":
        return <AdminManufacturingProcesses />;

      case "Public Pages":
        return <AdminPublicPagesManager />;

      case "Cite This Tool":
        return <AdminToolCitation />;

      case "Events":
        return <AdminEventsManager />;

      case "Submissions":
        return (
          <>
            {/* Keep list mounted so filters/search/pagination persist on "Back" */}
            <div style={{ display: selectedSubmissionId ? "none" : "block" }}>
              <AdminSubmissionList
                onOpenSubmission={(id: string) => {
                  setSelectedSubmissionId(id);
                }}
              />
            </div>

            {selectedSubmissionId && (
              <AdminSubmissionDetail
                submissionId={selectedSubmissionId}
                onBack={() => setSelectedSubmissionId(null)}
              />
            )}
          </>
        );
      default:
        return (
          <div className="admin-section">
            <h2>{activeSection}</h2>
            <p>Section under construction...</p>
          </div>
        );
    }
  };

  return (
    <div className="dashboard-container">
      {/* HEADER */}
      <DashboardHeader />

      <div className="dashboard-body">
        {/* SIDEBAR */}
        <div className={`sidebar-wrapper ${isSidebarOpen ? "open" : ""}`}>
          <SidebarAdmin
            activeSection={activeSection}
            onSelect={(section) => {
              setActiveSection(section);
              setIsSidebarOpen(false);
              setSelectedSubmissionId(null); // reset detail page when switching menu
            }}
          />
        </div>

        {/* MOBILE TOGGLE */}
        <button
          className="menu-toggle"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        >
          ☰
        </button>

        {/* MAIN CONTENT */}
        <main className="dashboard-content">
          <div className="content-wrapper">{renderContent()}</div>
        </main>
      </div>

      {/* FOOTER */}
      <DashboardFooter />
    </div>
  );
};

/** React component — Admin Dashboard. */
export default AdminDashboard;